import { Environment } from "@omni/types/enums/environment.enum";
import { ModelMapper } from "@omni/types/utils/modelMapper";
import { $ApiModuleName, $DataModuleName } from "@omni/types/utils/store";
import { hasWindow } from "@omni/utils/dom.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import { Axios, AxiosRequestConfig, AxiosResponseTransformer } from "axios";
import { merge } from "lodash-es";
import { Reducer } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { $LoadingModuleName } from "@/redux/modules/api/loading/loading.types";
import defaultReducer from "@/redux/modules/default.reducer";
import { getTranslations } from "@/redux/modules/value/translations/translations.selector";
import { Config } from "@/utils/config.utils";
import { ScrollOffsets } from "@/utils/enums";
import { getRequestErrorMessage, shouldSkip } from "@/utils/errors";
import { isEqual } from "@/utils/general";
import { scrollToElementById } from "@/utils/scroll";

import {
    IApiDataState,
    IState,
    TActionOptions,
    TApiAction,
    TApiDataStateSlice,
    TExtraThunkArgs
} from "./store.types";
import { createConstants } from "./utils";

export enum API_STAGES {
    Init = "Init",
    Reset = "Reset",
    Pending = "Pending",
    Failure = "Failure",
    Success = "Success"
}

type LoadActionParams = Partial<{
    path: {
        bff?: boolean;
        url: string;
    };
    headers: AxiosRequestConfig<any>["headers"];
    data: any;
}>;

type DefaultPathParams = {
    url: string;
};

type TLoadActionOptions<TResponse = any, TModel = any> = {
    reducer?: typeof defaultReducer | null;
    mapper?: ModelMapper<TResponse, TModel>;
    mergeParams?: boolean;
    transformResponse?: AxiosResponseTransformer | AxiosResponseTransformer[];
    initialState?: {};
    clearOnFetch?: boolean;
    clearOnError?: boolean;
    debounce?: boolean;
};

export const PREFIX = "api";

const DEFAULT_OPTIONS = {
    reducer: null,
    initialState: {},
    clearOnFetch: false,
    clearOnError: false,
    debounce: false,
    mergeParams: true,
    transformResponse: undefined
};

const createReducer = (
    constants: {
        readonly RESET: string;
        readonly PENDING: string;
        readonly FAILURE: string;
        readonly SUCCESS: string;
    },
    options: any
): Reducer<IApiDataState[keyof IApiDataState], TApiAction> => {
    const initialState: TApiDataStateSlice = {
        ...options.initialState,
        error: null,
        data: null,
        params: null
    };

    return (
        state: TApiDataStateSlice = { ...initialState },
        action: TApiAction
    ) => {
        if (options.reducer) {
            return options.reducer(
                state,
                action,
                constants,
                options,
                initialState
            );
        }

        return defaultReducer(state, action, constants, options, initialState);
    };
};

const createLoadAction =
    (
        namespace: keyof IApiDataState,
        getPath: Function,
        constants: {
            readonly RESET: string;
            readonly PENDING: string;
            readonly FAILURE: string;
            readonly SUCCESS: string;
        },
        options: TLoadActionOptions
    ) =>
    <TActionPayload extends { message?: string; error?: any }>(
        params: LoadActionParams = {},
        method: string = "GET",
        force: boolean = false,
        actionOptions: TActionOptions = { mergeParams: true }
    ) =>
    async (
        dispatch: ThunkDispatch<
            IState,
            TExtraThunkArgs,
            TApiAction<TActionPayload>
        >,
        getState: () => IState,
        client: Axios
    ): Promise<TApiAction<TActionPayload>> => {
        const storeState = getState();
        const state = storeState[$ApiModuleName][$DataModuleName][namespace];
        const loadingState = storeState[$ApiModuleName][$LoadingModuleName];
        // Merge with current params
        if (actionOptions.mergeParams && options.mergeParams) {
            params = merge({}, state?.params, params);
        }

        const isLoading = actionOptions?.discriminator
            ? state?.data &&
              (state.data as Record<string, any>)[actionOptions.discriminator]
                  ?.loading
            : loadingState?.[namespace];

        // Skip if loading still
        if (!force && isLoading) {
            return Promise.resolve({
                type: constants.PENDING,
                payload: {} as TActionPayload,
                actionOptions
            });
        }

        // Skip if same
        const stateParams = actionOptions.discriminator
            ? state &&
              (state as Record<string, any>)[actionOptions.discriminator]
                  ?.params
            : state?.params;

        if (!force && isEqual(stateParams, params) && !state?.error) {
            return Promise.resolve({
                type: constants.SUCCESS,
                payload: state?.data as TActionPayload,
                actionOptions
            });
        }

        // Dispatch loading action
        dispatch({
            type: constants.PENDING,
            payload: params as TActionPayload,
            actionOptions
        });

        const request: AxiosRequestConfig<any> = {
            url: getPath(params.path),
            method: method,
            headers: params.headers,
            data: params.data,
            transformResponse: undefined
        };

        const responseTransformations: Array<AxiosResponseTransformer> = [];

        // Mapper first, order matters! - first in array = first executed on response
        if (options.mapper) {
            const wrappedMapper: AxiosResponseTransformer = (
                data,
                _responseHeaders,
                status
            ) => {
                if (status && status >= 200 && status < 300) {
                    if (data === "") {
                        // If there is no data, do not try to parse it, otherwise JSON.parse will trip and cause the action to fail.
                        return options.mapper!(data);
                    }

                    return options.mapper!(JSON.parse(data));
                }
                return data;
            };

            responseTransformations.push(wrappedMapper);
        }

        if (
            options.transformResponse &&
            client.defaults.transformResponse &&
            Array.isArray(client.defaults.transformResponse)
        ) {
            responseTransformations.push(
                ...(client.defaults.transformResponse || []).concat(
                    options.transformResponse
                )
            );
        }

        if (responseTransformations.length > 0) {
            request.transformResponse = responseTransformations;
        }
        client.interceptors.request.clear();

        client.interceptors.request.use((config) => {
            if (!params.path?.bff) return config;

            config.baseURL =
                Config.general.environment === Environment.Local
                    ? Config.general["bff-base-url"]
                    : `${config.baseURL}/web`;

            return config;
        });

        try {
            const response = await client.request(request);
            const { data } = response;
            if (response.status === 303) {
                location.assign(response?.headers["x-hubo-location"]);
            }

            return dispatch({
                type: constants.SUCCESS,
                payload: data,
                actionOptions
            });
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                if (Config.general["authentication-required"]) {
                    LocalStorage.setItem("previousUrl", location.href);
                    location.assign(Config.getLoginPage());
                }
            }

            const { config, data, status } = error?.response || {};
            const { url, method } = config || {};

            if (config && status && url && method) {
                const payload = typeof data === "object" ? data.message : data;
                const strippedUrl = url.split("?")[0];

                const message = getRequestErrorMessage(
                    getTranslations(storeState),
                    payload,
                    strippedUrl,
                    method,
                    status
                );

                if (!shouldSkip(actionOptions?.skipErrors, status)) {
                    setTimeout(() => {
                        scrollToElementById(
                            "errorMessage",
                            ScrollOffsets.DEFAULT,
                            true
                        );
                    }, 1000);

                    import("@/redux/modules/value/errors/errors.module").then(
                        (mod) =>
                            dispatch(
                                mod.setError({
                                    url: strippedUrl,
                                    message,
                                    method
                                })
                            )
                    );
                }

                return dispatch({
                    type: constants.FAILURE,
                    payload: {
                        error,
                        message
                    } as TActionPayload,
                    actionOptions
                });
            }

            return dispatch({
                type: constants.FAILURE,
                payload: {
                    error
                } as TActionPayload,
                actionOptions
            });
        }
    };

export default <TPathParams = DefaultPathParams>(
    namespace: keyof IApiDataState,
    getPath: (pathParams: TPathParams) => string,
    options: TLoadActionOptions = {}
) => {
    options = {
        ...DEFAULT_OPTIONS,
        ...options
    };

    const constants = createConstants(API_STAGES, PREFIX, namespace);

    const reducer = createReducer(constants, options);

    try {
        if (hasWindow()) {
            window?.storeManager.reducerManagers.api.data.add(
                namespace,
                reducer
            );
        }
    } catch (e) {
        console.log(`ERROR-API::${namespace}`);
        console.error(e);
    }

    const load = createLoadAction(namespace, getPath, constants, options);

    const actions = {
        clear: () => (dispatch: ThunkDispatch<IState, any, TApiAction>) =>
            dispatch(() => ({
                type: constants.RESET,
                payload: {}
            })),
        load,
        refetch: <
            TActionPayload extends { message?: string; error?: any } = any
        >() => load<TActionPayload>(undefined, undefined, true)
    };

    return {
        constants,
        actions
    };
};
